import { loadStripe } from '@stripe/stripe-js';
import type { Stripe, StripeElements } from '@stripe/stripe-js';
import type { SupabaseClient } from '@supabase/supabase-js';

const PRESET_CENTS = [500, 1000, 2000, 4000];
const MIN_CENTS = 100;
const MAX_CENTS = 20000;

const fmtMoney = (cents: number) => `$${(cents / 100).toFixed(2)}`;

interface OpenOptions {
  /** A suggested top-up amount (e.g. machine price when wallet is short). */
  suggestCents?: number;
}

export async function openTopupModal(supabase: SupabaseClient, opts: OpenOptions = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) { alert('Please sign in first.'); return; }

  // Pull current balance so we can show it in the modal header.
  const { data: userRow } = await supabase
    .from('users')
    .select('wallet_balance_cents')
    .eq('id', session.user.id)
    .single();
  const currentBalance = userRow?.wallet_balance_cents ?? 0;

  const initialSelection = pickInitialSelection(opts.suggestCents);
  const modal = renderModal(currentBalance, initialSelection);
  document.body.appendChild(modal);

  let stripe: Stripe | null = null;
  let elements: StripeElements | null = null;
  let mountedClientSecret: string | null = null;
  let chosenCents: number = initialSelection.cents;

  const submitBtn = modal.querySelector<HTMLButtonElement>('#topup-submit')!;
  const errEl = modal.querySelector<HTMLElement>('#topup-error')!;
  const cardEl = modal.querySelector<HTMLElement>('#card-element')!;
  const customWrap = modal.querySelector<HTMLElement>('#custom-wrap')!;
  const customInput = modal.querySelector<HTMLInputElement>('#custom-amount-input')!;
  const customToggle = modal.querySelector<HTMLButtonElement>('#custom-toggle')!;

  const setSubmitText = () => { submitBtn.textContent = `Add ${fmtMoney(chosenCents)}`; };
  setSubmitText();

  // Mount/refresh Stripe Elements for the chosen amount.
  const mountStripe = async () => {
    submitBtn.disabled = true;
    submitBtn.textContent = 'Loading…';
    errEl.textContent = '';
    cardEl.innerHTML = '<div class="tiny" style="text-align:center;padding:18px;">Loading payment form…</div>';

    const res = await fetch('/api/topup', {
      method: 'POST',
      headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ amount_cents: chosenCents }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      errEl.textContent = 'Top-up init failed: ' + ((err as any).error ?? res.status);
      submitBtn.disabled = false;
      setSubmitText();
      return;
    }
    const { client_secret, publishable_key, connected_account_id } = await res.json() as any;

    if (!stripe) {
      stripe = await loadStripe(publishable_key, { stripeAccount: connected_account_id });
      if (!stripe) { errEl.textContent = 'Stripe failed to load'; return; }
    }
    cardEl.innerHTML = '';
    elements = stripe.elements({ clientSecret: client_secret });
    elements.create('payment', { layout: 'tabs' }).mount('#card-element');
    mountedClientSecret = client_secret;
    submitBtn.disabled = false;
    setSubmitText();
  };

  // Selecting a chip swaps the chosen amount.
  modal.querySelectorAll<HTMLButtonElement>('.amount-chip').forEach(chip => {
    chip.addEventListener('click', () => {
      const cents = Number(chip.dataset.cents);
      if (!cents) return;
      chosenCents = cents;
      modal.querySelectorAll('.amount-chip').forEach(c => c.classList.remove('active'));
      chip.classList.add('active');
      customWrap.classList.remove('show');
      customToggle.textContent = 'Custom amount';
      mountStripe();
    });
  });

  customToggle.addEventListener('click', () => {
    const isOpen = customWrap.classList.toggle('show');
    if (isOpen) {
      modal.querySelectorAll('.amount-chip').forEach(c => c.classList.remove('active'));
      customToggle.textContent = 'Use a preset';
      customInput.value = (chosenCents / 100).toString();
      customInput.focus();
    } else {
      customToggle.textContent = 'Custom amount';
    }
  });

  let customDebounce: number | undefined;
  customInput.addEventListener('input', () => {
    const n = Number(customInput.value);
    if (!isFinite(n)) return;
    const cents = Math.round(n * 100);
    if (cents < MIN_CENTS || cents > MAX_CENTS) {
      submitBtn.disabled = true;
      submitBtn.textContent = `Min ${fmtMoney(MIN_CENTS)} · Max ${fmtMoney(MAX_CENTS)}`;
      return;
    }
    chosenCents = cents;
    setSubmitText();
    if (customDebounce !== undefined) clearTimeout(customDebounce);
    customDebounce = window.setTimeout(mountStripe, 600);
  });

  modal.querySelector('#topup-cancel')!.addEventListener('click', () => modal.remove());

  submitBtn.addEventListener('click', async () => {
    if (!stripe || !elements || !mountedClientSecret) {
      errEl.textContent = 'Payment form is not ready yet.';
      return;
    }
    submitBtn.disabled = true;
    submitBtn.textContent = 'Processing…';
    errEl.textContent = '';

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: 'if_required',
    });
    if (error) {
      errEl.textContent = error.message ?? 'Payment failed';
      submitBtn.disabled = false;
      setSubmitText();
      return;
    }
    modal.querySelector('.modal-card')!.innerHTML = `
      <h2 style="text-align:center;">Top-up successful</h2>
      <p class="muted" style="text-align:center;margin-top:12px;">
        ${fmtMoney(chosenCents)} added. Your wallet will refresh in a moment.
      </p>
    `;
    setTimeout(() => modal.remove(), 2200);
  });

  // Kick off the first Stripe Element mount.
  mountStripe();
}

function pickInitialSelection(suggestCents?: number): { cents: number; usePreset: boolean } {
  if (suggestCents && suggestCents > 0) {
    // Pick the smallest preset >= suggestion (so a $2 machine offers $5 as default)
    const fit = PRESET_CENTS.find(p => p >= suggestCents);
    return { cents: fit ?? PRESET_CENTS[1], usePreset: !!fit };
  }
  return { cents: PRESET_CENTS[1], usePreset: true };
}

function renderModal(currentBalanceCents: number, initial: { cents: number; usePreset: boolean }) {
  const modal = document.createElement('div');
  modal.className = 'modal-backdrop';
  modal.id = 'topup-modal';
  modal.innerHTML = `
    <div class="modal-card">
      <h2>Add funds</h2>
      <p class="balance-line">Current balance: <strong>${fmtMoney(currentBalanceCents)}</strong></p>

      <div class="amount-grid" id="amount-grid">
        ${PRESET_CENTS.map(c =>
          `<button type="button" class="amount-chip ${initial.usePreset && c === initial.cents ? 'active' : ''}" data-cents="${c}">${fmtMoney(c)}</button>`
        ).join('')}
      </div>
      <button type="button" class="custom-toggle" id="custom-toggle">Custom amount</button>
      <div class="custom-amount" id="custom-wrap">
        <label for="custom-amount-input">Enter amount (${fmtMoney(MIN_CENTS)}–${fmtMoney(MAX_CENTS)})</label>
        <div class="input-with-prefix">
          <input type="number" id="custom-amount-input" min="1" max="200" step="0.01" inputmode="decimal" placeholder="0.00">
        </div>
      </div>

      <div class="test-card-hint">
        <strong>Test card:</strong> 4242 4242 4242 4242 · any future date · any CVC · any zip
      </div>

      <div id="card-element"></div>

      <div class="btn-stack" style="margin-top: 8px;">
        <button class="primary" id="topup-submit" disabled>Add ${fmtMoney(initial.cents)}</button>
        <button class="ghost" id="topup-cancel">Cancel</button>
      </div>
      <div class="error" id="topup-error"></div>
    </div>
  `;
  return modal;
}
